import { useCallback, useMemo } from "react"
import {
  createFileRoute,
  redirect,
  useNavigate,
  useParams,
} from "@tanstack/react-router"
import { useQuery } from "@tanstack/react-query"
import { BedDouble, Download } from "lucide-react"
import {
  CollectionView,
  type CollectionConfig,
} from "@/components/collection/CollectionView"
import type { ColumnSpec } from "@/lib/column-layout"
import {
  parseRouteFocus,
  RoutedCollectionLayout,
  useRouteRecordSelection,
} from "@/lib/routed-collection-layout"
import { LodgingBookingDetail } from "@/components/travel/LodgingBookingDetail"
import { downloadMappedCsv } from "@/lib/csv"
import { resolveEventId, useActiveEvent } from "@/core/active-event"
import { useRightRail } from "@/lib/right-rail"
import {
  buildLodging,
  formatDateTime,
  lodgingQueryOptions,
  lodgingStatusLabel,
  occupantsLabel,
  type LodgingBooking,
} from "@/core/lodging"

// The Lodging LAYOUT route. Route DEPTH = Main columns (mirrors forms.tsx):
//   /lodging              → col 1 (table)
//   /lodging/$bookingId   → col 1 + 2 (+ booking detail)
// `$bookingId` lives on a MARKER child route; ColumnLayout decides how many fit.
// `?focus=2` fullscreens col 2. Legacy `?bookingId=…` redirects into the path.
export const Route = createFileRoute("/$orgSlug/$eventSlug/lodging")({
  validateSearch: (s: Record<string, unknown>): { focus?: number; bookingId?: string } => {
    return {
      focus: parseRouteFocus(s.focus),
      bookingId: typeof s.bookingId === "string" ? s.bookingId : undefined,
    }
  },
  beforeLoad: ({ params, search }) => {
    if (search.bookingId) {
      throw redirect({
        to: "/$orgSlug/$eventSlug/lodging/$bookingId",
        params: { ...params, bookingId: search.bookingId },
        search: { focus: search.focus },
      })
    }
  },
  loader: ({ context, params }) => {
    void resolveEventId(params.orgSlug, params.eventSlug).then((eventId) => {
      if (!eventId) return
      void context.queryClient
        .ensureQueryData(lodgingQueryOptions(eventId))
        .catch(() => undefined)
    })
  },
  component: LodgingView,
})

const STATUS_PILL: Record<LodgingBooking["status"], string> = {
  upcoming: "bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-400",
  checked_in: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400",
  checked_out: "bg-plum-100 text-plum-700/60 dark:bg-white/10 dark:text-plum-200/45",
}

function LodgingView() {
  const params = useParams({ from: "/$orgSlug/$eventSlug/lodging" })
  const { focus } = Route.useSearch()
  const navigate = useNavigate()
  const { eventId } = useActiveEvent()
  const showInRail = useRightRail((s) => s.show)

  const { bookingId: selectedId } = useRouteRecordSelection("bookingId")

  const openBooking = useCallback(
    (id: string) =>
      void navigate({
        to: "/$orgSlug/$eventSlug/lodging/$bookingId",
        params: { ...params, bookingId: id },
        search: {},
      }),
    [navigate, params],
  )
  const closePane = useCallback(
    () => void navigate({ to: "/$orgSlug/$eventSlug/lodging", params, search: {} }),
    [navigate, params],
  )

  const lodgingQ = useQuery({ ...lodgingQueryOptions(eventId ?? ""), enabled: !!eventId })
  const bookings = useMemo(() => buildLodging(lodgingQ.data), [lodgingQ.data])
  const selected = useMemo(
    () => (selectedId ? bookings.find((b) => b.id === selectedId) ?? null : null),
    [bookings, selectedId],
  )

  const config = useMemo<CollectionConfig<LodgingBooking>>(() => {
    const exportCsv = (rows: LodgingBooking[]) => {
      downloadMappedCsv(
        "lodging.csv",
        ["Hotel", "Location", "Guest", "Check-in", "Check-out", "Nights", "Status", "Confirmation"],
        rows,
        (b) => [
          b.hotelName,
          b.location ?? "",
          occupantsLabel(b.occupants),
          b.checkInAt ?? "",
          b.checkOutAt ?? "",
          b.nights ?? "",
          lodgingStatusLabel(b.status),
          b.confirmation ?? "",
        ],
      )
    }

    return {
      title: "Lodging",
      getId: (b) => b.id,
      searchText: (b) =>
        `${b.hotelName} ${b.location ?? ""} ${b.confirmation ?? ""} ${b.occupants
          .map((o) => o.name)
          .join(" ")}`,
      onOpen: (b) => openBooking(b.id),
      defaultSortId: "checkin",
      defaultSortDir: "asc",
      emptyTitle: "No lodging booked",
      emptyBlurb: "Hotel and accommodation reservations appear here as they're added for this event.",
      emptyIcon: <BedDouble className="h-5 w-5" />,
      savedViews: [
        { id: "all", label: "All", filter: () => true },
        { id: "upcoming", label: "Upcoming", filter: (b) => b.status === "upcoming" },
        { id: "checked_in", label: "Checked-in", filter: (b) => b.status === "checked_in" },
        { id: "checked_out", label: "Checked-out", filter: (b) => b.status === "checked_out" },
      ],
      groupOptions: [
        {
          id: "hotel",
          label: "Hotel",
          groupOf: (b) => b.hotelName,
        },
        {
          id: "status",
          label: "Status",
          groupOf: (b) => lodgingStatusLabel(b.status),
        },
      ],
      menuActions: [
        {
          id: "export-all",
          label: "Export all (CSV)",
          icon: <Download className="mb-mii" />,
          run: (rows) => exportCsv(rows),
        },
      ],
      columns: [
        {
          id: "hotel",
          header: "Hotel",
          sortable: true,
          sortValue: (b) => b.hotelName.toLowerCase(),
          render: (b) => (
            <span className="flex items-center gap-2">
              <BedDouble className="h-3.5 w-3.5 shrink-0 text-plum-700/55 dark:text-plum-200/45" />
              <span className="min-w-0">
                <span className="block truncate font-semibold text-plum-900 dark:text-plum-50">
                  {b.hotelName}
                </span>
                {b.location && (
                  <span className="block truncate text-[11.5px] text-plum-700/55 dark:text-plum-200/45">
                    {b.location}
                  </span>
                )}
              </span>
            </span>
          ),
        },
        {
          id: "guest",
          header: "Guest",
          className: "text-plum-700/75 dark:text-plum-200/60",
          render: (b) => {
            const first = b.occupants[0]
            if (!first) return <span className="text-plum-700/40 dark:text-plum-200/30">—</span>
            return (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  showInRail({ kind: "guest", guestId: first.id, name: first.name })
                }}
                className="rounded text-left hover:text-plum-900 hover:underline dark:hover:text-plum-50"
              >
                {occupantsLabel(b.occupants)}
              </button>
            )
          },
        },
        {
          id: "checkin",
          header: "Check-in",
          className: "text-plum-700/60 dark:text-plum-200/45 tabular-nums",
          sortable: true,
          sortValue: (b) => b.checkInAt ?? "9999",
          render: (b) =>
            b.checkInAt ? (
              formatDateTime(b.checkInAt)
            ) : (
              <span className="text-plum-700/40 dark:text-plum-200/30">—</span>
            ),
        },
        {
          id: "checkout",
          header: "Check-out",
          className: "text-plum-700/60 dark:text-plum-200/45 tabular-nums",
          sortable: true,
          sortValue: (b) => b.checkOutAt ?? "9999",
          render: (b) =>
            b.checkOutAt ? (
              formatDateTime(b.checkOutAt)
            ) : (
              <span className="text-plum-700/40 dark:text-plum-200/30">—</span>
            ),
        },
        {
          id: "nights",
          header: "Nights",
          className: "text-plum-700/65 dark:text-plum-200/50 tabular-nums",
          sortable: true,
          sortValue: (b) => b.nights ?? 0,
          render: (b) => (b.nights != null ? b.nights : "—"),
        },
        {
          id: "status",
          header: "Status",
          render: (b) => (
            <span className={`rounded-md px-2 py-0.5 text-[11px] font-semibold ${STATUS_PILL[b.status]}`}>
              {lodgingStatusLabel(b.status)}
            </span>
          ),
        },
      ],
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openBooking, showInRail])

  const table = (
    <CollectionView
      config={config}
      rows={bookings}
      activeId={selectedId}
      loading={lodgingQ.isLoading && bookings.length === 0}
      error={
        lodgingQ.isError
          ? ((lodgingQ.error as Error)?.message ?? "Failed to load lodging")
          : lodgingQ.data && !lodgingQ.data.success
            ? "Couldn't load lodging."
            : null
      }
    />
  )

  const columns = useMemo<ColumnSpec[]>(() => {
    const cols: ColumnSpec[] = [{ id: "list", minWidth: 320, width: 420, node: table }]
    if (selected) {
      cols.push({
        id: "detail",
        minWidth: 460,
        fill: true,
        node: <LodgingBookingDetail booking={selected} onClose={closePane} closeVariant="close" />,
      })
    }
    return cols
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table, selected, closePane])

  return <RoutedCollectionLayout columns={columns} focus={focus} />
}
